/**
 * Front 영역 전용 인증 서비스
 * 
 * 모든 API 호출은 apiClient 인스턴스를 통해 처리됩니다.
 * - 개발 환경: setupProxy.js가 /api를 프록시 처리
 * - 프로덕션: apiClient.baseURL이 직접 API 서버를 가리킴
 */

import axios from 'axios';
import apiClient from '../../../core/http/axios';
import env from '../../../core/config/env';
import { 
  FrontLoginRequest, 
  FrontRegisterRequest, 
  FrontAuthResponse, 
  FrontUser,
  FrontUserPreferences,
  FrontNotificationSettings,
  FrontPrivacySettings,
  LoginResponse,
  RefreshTokenResponse,
  UserRole
} from '../types/auth';
import { decodeJWT, getRoleFromToken, getUserNoFromToken } from '../../../core/utils/jwt';
import {
  setAuthTokenCookie,
  getAuthTokenCookie,
  deleteAuthTokenCookie,
  setRefreshTokenStorage,
  getRefreshTokenStorage,
  deleteRefreshTokenStorage,
  getOrCreateDeviceId,
} from '../../../core/utils/cookie';

/* ========================================
 * Mock 사용자 데이터 (주석처리됨)
 * ======================================== */
// const mockUsers = [
//   {
//     id: '1',
//     name: '김학생',
//     email: 'student@example.com',
//     password: 'student123',
//     role: 'student' as const,
//     userNo: '10',
//     profile: {
//       nickname: '학생',
//       phone: '010-1234-5678',
//       birthDate: '2000-01-01',
//       gender: 'male' as const,
//       bio: '학생입니다!',
//     },
//     preferences: {
//       language: 'ko',
//       timezone: 'Asia/Seoul',
//       notifications: {
//         email: true,
//         push: true,
//         sms: false,
//         marketing: false,
//         updates: true,
//       },
//       privacy: {
//         profileVisibility: 'friends' as const,
//         showEmail: false,
//         showPhone: false,
//         showBirthDate: false,
//       },
//       theme: 'light' as const,
//     },
//     avatar: 'https://via.placeholder.com/100x100/34a853/ffffff?text=Student',
//     lastLoginAt: '2024-01-01T00:00:00Z',
//     createdAt: '2024-01-01T00:00:00Z',
//     updatedAt: '2024-01-01T00:00:00Z',
//     isVerified: true,
//   },
//   {
//     id: '2',
//     name: '이교수',
//     email: 'professor@example.com',
//     password: 'professor123',
//     role: 'professor' as const,
//     userNo: '20',
//     profile: {
//       nickname: '교수',
//       phone: '010-9876-5432',
//       birthDate: '1980-01-01',
//       gender: 'male' as const,
//       bio: '교수입니다!',
//     },
//     preferences: {
//       language: 'ko',
//       timezone: 'Asia/Seoul',
//       notifications: {
//         email: true,
//         push: true,
//         sms: false,
//         marketing: false,
//         updates: true,
//       },
//       privacy: {
//         profileVisibility: 'friends' as const,
//         showEmail: false,
//         showPhone: false,
//         showBirthDate: false,
//       },
//       theme: 'light' as const,
//     },
//     avatar: 'https://via.placeholder.com/100x100/4285f4/ffffff?text=Prof',
//     lastLoginAt: '2024-01-01T00:00:00Z',
//     createdAt: '2024-01-01T00:00:00Z',
//     updatedAt: '2024-01-01T00:00:00Z',
//     isVerified: true,
//   },
//   {
//     id: '3',
//     name: '김사용자',
//     email: 'user@toss.com',
//     password: 'user123',
//     role: 'user' as const,
//     userNo: '30',
//     profile: {
//       nickname: '토스러버',
//       phone: '010-1234-5678',
//       birthDate: '1990-01-01',
//       gender: 'male' as const,
//       bio: '토스를 사랑하는 사용자입니다!',
//     },
//     preferences: {
//       language: 'ko',
//       timezone: 'Asia/Seoul',
//       notifications: {
//         email: true,
//         push: true,
//         sms: false,
//         marketing: false,
//         updates: true,
//       },
//       privacy: {
//         profileVisibility: 'friends' as const,
//         showEmail: false,
//         showPhone: false,
//         showBirthDate: false,
//       },
//       theme: 'light' as const,
//     },
//     avatar: 'https://via.placeholder.com/100x100/34a853/ffffff?text=User',
//     lastLoginAt: '2024-01-01T00:00:00Z',
//     createdAt: '2024-01-01T00:00:00Z',
//     updatedAt: '2024-01-01T00:00:00Z',
//     isVerified: true,
//   },
// ];

/**
 * JWT 토큰에서 사용자 정보를 생성합니다
 */
const createUserFromToken = (token: string, userNo: string): FrontUser | null => {
  const role = getRoleFromToken(token);
  const payload = decodeJWT(token);
  
  if (!payload) {
    return null;
  }

  const defaultPreferences: FrontUserPreferences = {
    language: 'ko',
    timezone: 'Asia/Seoul',
    notifications: {
      email: true,
      push: true,
      sms: false,
      marketing: false,
      updates: true,
    },
    privacy: {
      profileVisibility: 'friends',
      showEmail: false,
      showPhone: false,
      showBirthDate: false,
    },
    theme: 'light',
  };

  // 토큰에서 username 추출 (sub 또는 username 필드)
  const username = payload.sub || payload.username || userNo;

  return {
    id: userNo,
    email: `${username}@genbook.pro`,
    name: role === 'professor' ? '교수' : '학생',
    role: role || 'student',
    profile: {
      nickname: username,
    },
    preferences: defaultPreferences,
    createdAt: new Date().toISOString(),
    isVerified: true,
  };
};

/* ========================================
 * Mock 함수들 (주석처리됨)
 * ======================================== */
// // Mock JWT 토큰 생성 (간단한 Base64 인코딩)
// const createMockJWT = (userNo: string, role: UserRole): string => {
//   const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
//   const payload = btoa(JSON.stringify({
//     user_no: userNo,
//     role: role,
//     exp: Math.floor(Date.now() / 1000) + 7200, // 2시간
//     iat: Math.floor(Date.now() / 1000),
//   }));
//   const signature = 'mock-signature';
//   return `${header}.${payload}.${signature}`;
// };

// // Mock 사용자 로그인
// const mockUserLogin = async (email: string, password: string): Promise<FrontAuthResponse> => {
//   return new Promise((resolve, reject) => {
//     setTimeout(() => {
//       const user = mockUsers.find(u => u.email === email && u.password === password);
//       
//       if (user) {
//         // JWT 형식의 토큰 생성
//         const accessToken = createMockJWT(user.userNo, user.role as UserRole);
//         
//         resolve({
//           user: {
//             id: user.id,
//             name: user.name,
//             email: user.email,
//             role: user.role,
//             profile: user.profile,
//             preferences: user.preferences,
//             avatar: user.avatar,
//             lastLoginAt: user.lastLoginAt,
//             createdAt: user.createdAt,
//             updatedAt: user.updatedAt,
//             isVerified: user.isVerified,
//           },
//           token: accessToken,
//           expiresIn: 7200, // 2시간
//         });
//       } else {
//         reject(new Error('이메일 또는 비밀번호가 올바르지 않습니다.'));
//       }
//     }, 1000);
//   });
// };

// // Mock 사용자 회원가입
// const mockUserRegister = async (data: FrontRegisterRequest): Promise<FrontAuthResponse> => {
//   return new Promise((resolve, reject) => {
//     setTimeout(() => {
//       const existingUser = mockUsers.find(u => u.email === data.email);
//       
//       if (existingUser) {
//         reject(new Error('이미 존재하는 이메일입니다.'));
//         return;
//       }

//       if (data.password !== data.confirmPassword) {
//         reject(new Error('비밀번호가 일치하지 않습니다.'));
//         return;
//       }

//       if (!data.termsAccepted || !data.privacyAccepted) {
//         reject(new Error('약관에 동의해야 합니다.'));
//         return;
//       }

//       const userId = Date.now().toString();
//       const newUser = {
//         id: userId,
//         name: data.name,
//         email: data.email,
//         password: data.password,
//         role: 'user' as const,
//         userNo: userId,
//         profile: {
//           nickname: data.name,
//           phone: '',
//           birthDate: '',
//           gender: 'male' as const,
//           bio: '',
//         },
//         preferences: {
//           language: 'ko',
//           timezone: 'Asia/Seoul',
//           notifications: {
//             email: true,
//             push: true,
//             sms: false,
//             marketing: false,
//             updates: true,
//           },
//           privacy: {
//             profileVisibility: 'friends' as const,
//             showEmail: false,
//             showPhone: false,
//             showBirthDate: false,
//           },
//           theme: 'light' as const,
//         },
//         avatar: `https://via.placeholder.com/100x100/ff6b6b/ffffff?text=${data.name.charAt(0)}`,
//         lastLoginAt: new Date().toISOString(),
//         createdAt: new Date().toISOString(),
//         updatedAt: new Date().toISOString(),
//         isVerified: false,
//       };

//       mockUsers.push(newUser);

//       resolve({
//         user: {
//           id: newUser.id,
//           name: newUser.name,
//           email: newUser.email,
//           role: newUser.role,
//           profile: newUser.profile,
//           preferences: newUser.preferences,
//           avatar: newUser.avatar,
//           lastLoginAt: newUser.lastLoginAt,
//           createdAt: newUser.createdAt,
//           updatedAt: newUser.updatedAt,
//           isVerified: newUser.isVerified,
//         },
//         token: `user-token-${newUser.id}-${Date.now()}`,
//         expiresIn: 7200,
//       });
//     }, 1000);
//   });
// };

// // Mock 현재 사용자 조회
// const mockGetCurrentUser = async (): Promise<FrontUser> => {
//   return new Promise((resolve, reject) => {
//     setTimeout(() => {
//       const token = localStorage.getItem('front-auth-storage');
//       if (!token) {
//         reject(new Error('사용자 토큰이 없습니다.'));
//         return;
//       }

//       try {
//         const authData = JSON.parse(token);
//         const accessToken = authData.state?.token;
//         
//         if (!accessToken) {
//           reject(new Error('토큰이 없습니다.'));
//           return;
//         }

//         // JWT 토큰에서 사용자 정보 추출
//         const userNo = getUserNoFromToken(accessToken);
//         if (!userNo) {
//           reject(new Error('토큰에서 사용자 번호를 추출할 수 없습니다.'));
//           return;
//         }

//         const user = createUserFromToken(accessToken, userNo);
//         if (!user) {
//           reject(new Error('사용자 정보를 생성할 수 없습니다.'));
//           return;
//         }

//         resolve(user);
//       } catch (error) {
//         reject(new Error('사용자 토큰이 유효하지 않습니다.'));
//       }
//     }, 500);
//   });
// };

// 실제 API 호출 함수들
const userLogin = async (data: FrontLoginRequest): Promise<FrontAuthResponse> => {
  // console.log('🔍 Front 로그인 시도:', { 
  //   username: data.email, // email 필드를 username으로 사용
  //   env: env.REACT_APP_ENV,
  //   nodeEnv: process.env.NODE_ENV
  // });

  /* ========================================
   * Mock 로그인 로직 (주석처리됨)
   * ======================================== */
  // if (env.REACT_APP_USE_MOCK) {
  //   console.log('📝 Mock 데이터로 로그인 처리');
  //   return mockUserLogin(data.email, data.password);
  // }

  // console.log('🌐 실제 API로 로그인 처리');
  
  // application/x-www-form-urlencoded 형식으로 요청
  const formData = new URLSearchParams();
  formData.append('grant_type', 'password');
  formData.append('username', data.email); // email 필드를 username으로 사용
  formData.append('password', data.password);
  formData.append('scope', '');
  formData.append('client_id', 'string');
  formData.append('client_secret', 'string');

  try {
    const response = await apiClient.post<LoginResponse>(
      '/auth/login',
      formData.toString(),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
      }
    );

    const { access_token, refresh_token } = response.data;
    
    // 토큰을 쿠키에 저장
    setAuthTokenCookie(access_token, 2); // 2시간
    // console.log('🍪 토큰이 쿠키에 저장되었습니다');
    if (refresh_token) {
      setRefreshTokenStorage(refresh_token);
    }
    
    // JWT 토큰에서 사용자 정보 추출
    const userNo = getUserNoFromToken(access_token);
    const role = getRoleFromToken(access_token);
    
    if (!userNo) {
      // userNo가 없으면 username을 사용
      const payload = decodeJWT(access_token);
      const username = payload?.sub || data.email;
      
      const user = createUserFromToken(access_token, username);
      if (!user) {
        throw new Error('사용자 정보를 생성할 수 없습니다.');
      }
      
      if (role) {
        user.role = role;
      }

      return {
        user,
        token: access_token,
        expiresIn: 7200,
      };
    }

    // 사용자 정보 생성
    const user = createUserFromToken(access_token, userNo);
    
    if (!user) {
      throw new Error('사용자 정보를 생성할 수 없습니다.');
    }

    // 역할이 토큰에서 추출된 경우 업데이트
    if (role) {
      user.role = role;
    }

    return {
      user,
      token: access_token,
      expiresIn: 7200,
    };
  } catch (error: any) {
    // console.error('❌ 로그인 실패:', error);
    if (error.response?.status === 401 || error.response?.status === 400) {
      throw new Error('사용자명 또는 비밀번호가 올바르지 않습니다.');
    }
    throw new Error(error.response?.data?.detail || '로그인에 실패했습니다.');
  }
};

const userRegister = async (data: FrontRegisterRequest): Promise<FrontAuthResponse> => {
  /* ========================================
   * Mock 회원가입 로직 (주석처리됨)
   * ======================================== */
  // if (env.REACT_APP_USE_MOCK) {
  //   return mockUserRegister(data);
  // }

  const response = await apiClient.post('/auth/register', data);
  return response.data;
};

const getCurrentUser = async (): Promise<FrontUser> => {
  /* ========================================
   * Mock 현재 사용자 조회 로직 (주석처리됨)
   * ======================================== */
  // if (env.REACT_APP_USE_MOCK) {
  //   return mockGetCurrentUser();
  // }

  // 쿠키에서 토큰을 읽어서 사용자 정보 생성
  const token = getAuthTokenCookie();
  if (!token) {
    throw new Error('인증 토큰이 없습니다.');
  }

  const userNo = getUserNoFromToken(token);
  const payload = decodeJWT(token);
  const username = userNo || payload?.sub || 'unknown';

  const user = createUserFromToken(token, username);
  if (!user) {
    throw new Error('사용자 정보를 생성할 수 없습니다.');
  }

  return user;
};

const userLogout = async (): Promise<void> => {
  /* ========================================
   * Mock 로그아웃 로직 (주석처리됨)
   * ======================================== */
  // if (env.REACT_APP_USE_MOCK) {
  //   return Promise.resolve();
  // }

  // 쿠키/저장소에서 토큰 삭제
  deleteAuthTokenCookie();
  deleteRefreshTokenStorage();
  // console.log('🍪 쿠키에서 토큰이 삭제되었습니다');
};

/**
 * POST /auth/refresh로 access_token 갱신
 * 인터셉터 없는 axios 사용 (401 시 무한 루프 방지)
 */
const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getRefreshTokenStorage();
  if (!refreshToken) {
    throw new Error('리프레시 토큰이 없습니다.');
  }
  const deviceId = getOrCreateDeviceId();
  const baseURL = env.REACT_APP_API_URL || '/api';
  const response = await axios.post<RefreshTokenResponse>(
    `${baseURL}/auth/refresh`,
    {
      refresh_token: refreshToken,
      device_id: deviceId,
    },
    {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      timeout: 10000,
    }
  );
  const { access_token, expires_in } = response.data;
  const expiresInHours = expires_in ? expires_in / 3600 : 2;
  setAuthTokenCookie(access_token, expiresInHours);
  return access_token;
};

const setUserToken = (token: string): void => {
  apiClient.defaults.headers.common['X-User-Token'] = `Bearer ${token}`;
};

const frontAuthService = {
  login: userLogin,
  register: userRegister,
  getCurrentUser,
  logout: userLogout,
  setToken: setUserToken,
  refreshAccessToken,
};

export default frontAuthService;